import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { sendingOrgService, type SendingOrgRow } from "@/features/sending-org/services/sendingOrgService";
import { SouserSection } from "@/features/sending-org/components/SouserSection";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Modal } from "@/shared/components/ui/Modal";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";

type Tab = "sending-orgs" | "so-users";

const OFFICER_TYPES = ["officer", "admin"];

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const generateCode = (existing: string[]): string => {
  let code: string;
  do {
    code = Array.from({ length: 3 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join("");
  } while (existing.includes(code));
  return code;
};

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

interface FormState {
  code: string;
  descr: string;
  country_origin: string;
  sort_order: string;
  active: boolean;
}

const INIT_FORM: FormState = {
  code: "",
  descr: "",
  country_origin: "",
  sort_order: "0",
  active: true,
};

export const SendingOrgPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");
  const souserEnabled = userData?.bu_souser_enabled ?? false;

  const [activeTab, setActiveTab] = useState<Tab>("sending-orgs");

  const [records, setRecords] = useState<SendingOrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const countryLabel = useCallback(
    (code: string | null | undefined) =>
      code ? t(`profile.country_${code.toLowerCase()}`, { defaultValue: code }) : t("sending_org.no_country"),
    [t],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter(
      (r) =>
        r.code?.toLowerCase().includes(q) ||
        r.descr?.toLowerCase().includes(q) ||
        countryLabel(r.country_origin).toLowerCase().includes(q),
    );
  }, [records, search, countryLabel]);

  /** Records grouped by country, following COUNTRY_OPTIONS order; unknown countries last. */
  const grouped = useMemo(() => {
    const known = COUNTRY_OPTIONS.map((c) => ({
      countryCode: c.code,
      rows: filtered.filter((r) => r.country_origin === c.code),
    })).filter((g) => g.rows.length > 0);

    const knownCodes = new Set(COUNTRY_OPTIONS.map((c) => c.code));
    const other = filtered.filter((r) => !r.country_origin || !knownCodes.has(r.country_origin));

    return other.length > 0 ? [...known, { countryCode: null, rows: other }] : known;
  }, [filtered]);

  // Form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INIT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<SendingOrgRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const data = await sendingOrgService.getAll();
      setRecords(data);
    } catch {
      showToast(t("sending_org.load_error"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    if (isOfficer) {
      loadRecords();
    } else {
      setLoading(false);
    }
  }, [isOfficer, loadRecords]);

  const openCreate = () => {
    setEditingCode(null);
    const existingCodes = records.map((r) => r.code).filter(Boolean) as string[];
    setForm({ ...INIT_FORM, code: generateCode(existingCodes) });
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (row: SendingOrgRow) => {
    setEditingCode(row.code);
    setForm({
      code: row.code,
      descr: row.descr || "",
      country_origin: row.country_origin || "",
      sort_order: String(row.sort_order ?? 0),
      active: row.active ?? true,
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const handleSave = async () => {
    setFormError("");

    if (!form.descr.trim()) {
      setFormError(t("sending_org.descr_required"));
      return;
    }
    if (!form.country_origin) {
      setFormError(t("sending_org.country_origin_required"));
      return;
    }

    const payload = {
      descr: form.descr.trim() || null,
      country_origin: form.country_origin || null,
      sort_order: parseInt(form.sort_order, 10) || 0,
      active: form.active,
      msgnbr: null,
      msgset: null,
    };

    setSaving(true);
    try {
      if (editingCode) {
        await sendingOrgService.update(editingCode, payload);
      } else {
        await sendingOrgService.create({ code: form.code.trim().toUpperCase(), ...payload });
      }
      showToast(t("sending_org.save_success"), "success");
      setModalOpen(false);
      await loadRecords();
    } catch {
      setFormError(t("sending_org.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await sendingOrgService.delete(deleteTarget.code);
      showToast(t("sending_org.delete_success"), "success");
      setDeleteTarget(null);
      await loadRecords();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t("sending_org.delete_error");
      showToast(msg, "error");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // ── Permission guard ───────────────────────────────────────────────────────

  if (!isOfficer) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-sm text-gray-500">{t("common.permission_denied")}</p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 pt-3 border-b border-gray-200 mb-4 md:mb-6">
        <div className="w-full">
          {/* Top row: back + title + action */}
          <div className="flex items-center justify-between gap-3 pb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/other-setup")}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg md:text-xl font-bold text-gray-900">{t("sending_org.title")}</h1>
                {activeTab === "sending-orgs" && !loading && (
                  <p className="text-xs text-gray-400">
                    {search.trim()
                      ? `${filtered.length} / ${records.length} ${t("sending_org.count_label")}`
                      : `${records.length} ${t("sending_org.count_label")}`}
                  </p>
                )}
              </div>
            </div>
            {activeTab === "sending-orgs" && (
              <Button size="sm" onClick={openCreate}>
                {t("sending_org.new_record")}
              </Button>
            )}
          </div>

          {/* Tab switcher */}
          <div className="flex gap-1 -mb-px">
            {(["sending-orgs", ...(souserEnabled ? ["so-users"] : [])] as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab === "sending-orgs" ? "Sending Orgs" : "SO Users"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SO Users tab */}
      {activeTab === "so-users" && souserEnabled && (
        <div className="w-full">
          <SouserSection />
        </div>
      )}

      {/* Sending Orgs tab */}
      {activeTab === "sending-orgs" && <div className="w-full">
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Search bar */}
          {!loading && records.length > 0 && (
            <div className="px-4 md:px-6 py-3 border-b border-gray-100">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("common.search_e")}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-10">
              <Spinner />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {t("sending_org.no_records")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              {t("sending_org.no_search_results")}
            </div>
          ) : (
            <div>
              {grouped.map((group, gi) => (
                <div key={group.countryCode ?? "__other__"}>
                  {/* Country header */}
                  <div className={`px-4 md:px-6 py-2 flex items-center gap-2 bg-gray-50 ${gi > 0 ? "border-t border-gray-200" : ""}`}>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {countryLabel(group.countryCode)}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono">
                      {group.rows.length}
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {group.rows.map((row) => (
                      <li key={row.code} className="flex items-start gap-3 px-4 md:px-6 py-4">
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900 font-mono">{row.code}</p>
                            {row.descr && (
                              <p className="text-sm text-gray-700 truncate">{row.descr}</p>
                            )}
                            {(row.sort_order ?? 0) > 0 && (
                              <span className="text-[10px] text-gray-400 font-mono">#{row.sort_order}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                row.active
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {row.active ? t("settings.status_active") : t("settings.status_inactive")}
                            </span>
                          </div>
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0 pt-0.5">
                          <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
                            {t("common.edit")}
                          </Button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(row)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title={t("common.delete")}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingCode ? t("sending_org.edit_record") : t("sending_org.new_record")}
        headerAction={
          <Button size="sm" onClick={handleSave} loading={saving}>
            {t("common.save")}
          </Button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("sending_org.code")}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 text-sm font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg select-none">
              {form.code}
              {!editingCode && (
                <span className="ml-auto text-[10px] font-sans font-normal text-gray-400">
                  {t("sending_org.code_auto")}
                </span>
              )}
            </div>
          </div>
          <Input
            label={t("sending_org.descr")}
            placeholder={t("sending_org.descr_placeholder")}
            value={form.descr}
            onChange={(e) => setForm((f) => ({ ...f, descr: e.target.value }))}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("sending_org.country_origin")}
              </label>
              <select
                value={form.country_origin}
                onChange={(e) => setForm((f) => ({ ...f, country_origin: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                <option value="">{t("profile.select_country")}</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {t(`profile.${c.key}`)}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label={t("sending_org.sort_order")}
              type="number"
              min={0}
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("sending_org.active")}
          </label>

          {formError && <p className="text-sm text-red-600">{formError}</p>}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        title={t("common.confirm_delete")}
      >
        <div className="space-y-4">
          {deleteTarget && (
            <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-3 py-2 font-mono">
              {deleteTarget.code}{deleteTarget.descr ? ` — ${deleteTarget.descr}` : ""}
            </p>
          )}
          <p className="text-sm text-gray-600">{t("sending_org.confirm_delete")}</p>
          <div className="flex gap-3 justify-end pt-1">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              {t("common.delete")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
